/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from "react";

import { useNavigate, useParams } from "react-router-dom";

import { useTranslation } from "react-i18next";

import { useGetQuestionnaireQuery } from "@/pages/Public/redux/questionnaire/questionnaire.api";

import { BusinessInfoSelection, InitialAnswer, PostBusinessFlow } from "@/types/questionnaire";
import { useLazyGetToDosByFiltersQuery } from "@/pages/Private/redux/to-do/to-do.api";

import { ROUTE_CONFIG } from "@/routes/config";

import { ToDoInfo } from "@/pages/Private/pages/ToDo/ToDoList/schema/to-do";

import CybersecurityPrompt from "./components/CheckCybersecurity/CybersecurityPrompt";
import BusinessInfoFlow from "./components/BusinessInfoSteps/BusinessInfoFlow";
import NeedsAnalysisFlow from "./components/NeedsAnalysisSteps/NeedsAnalysisFlow";
import SelfAssessmentFlow from "./components/SelfAssessmentSteps/SelfAssessmentFlow";
import { Footer } from "@/components/Footer/Footer";

const { REGISTRATION, HOME } = ROUTE_CONFIG;

export default function Questionnaire() {
	const { initial } = useParams();
	const [questionFlow, setQuestionFlow] = useState<
		"initial" | "check-cybersecurity" | "business-info"
	>("initial");
	const navigate = useNavigate();
	const { t } = useTranslation();

	const { data: questionnaireData } = useGetQuestionnaireQuery();

	const [initialAnswer, setInitialAnswer] = useState<InitialAnswer>(
		(initial as InitialAnswer) ?? null
	);
	const [postBusinessFlow, setPostBusinessFlow] = useState<PostBusinessFlow>(null);

	const [selection, setSelection] = useState<BusinessInfoSelection | null>(null);

	const [toDos, setToDos] = useState<ToDoInfo[]>([]);

	const [getToDos] = useLazyGetToDosByFiltersQuery();

	useEffect(() => {
		const fetchToDos = async () => {
			const response = await getToDos({
				industryIds: selection?.industryIds ?? [],
				companySizeIds: selection?.companySizeId ? [selection.companySizeId] : [],
				companyRevenueIds: selection?.companyRevenueId ? [selection.companyRevenueId] : [],
				companyBalanceIds: selection?.companyBalanceId ? [selection.companyBalanceId] : [],
			});

			setToDos(response.data ?? []);
		};

		if (selection) {
			fetchToDos();

			localStorage.setItem("selection", JSON.stringify(selection));
		}
	}, [selection]);

	const handleStart = (answer: "yes" | "no" | "unknown") => {
		setInitialAnswer(answer);

		switch (answer) {
			case "yes":
			case "unknown":
				setQuestionFlow("business-info");
				break;
			case "no":
				setQuestionFlow("check-cybersecurity");
				break;
		}
	};

	useEffect(() => {
		if (initial && ["yes", "no", "unknown"].includes(initial)) {
			const prevSelection = localStorage.getItem("selection");
			const prevSelfAssessmentAnswers = localStorage.getItem("selfAssessmentAnswers");

			if (prevSelection) {
				localStorage.removeItem("selection");
			}

			if (prevSelfAssessmentAnswers) {
				localStorage.removeItem("selfAssessmentAnswers");
			}

			handleStart(initial as "yes" | "no" | "unknown");
		} else {
			navigate(HOME);
		}
	}, [initial]);

	const filteredNeedsAnalysis = useMemo(() => {
		if (!selection || !questionnaireData) {
			return [];
		}

		return questionnaireData.filter((step) => {
			const industryMatch =
				step?.industryIds?.some((id) => selection.industryIds.includes(id)) ?? false;
			const sizeMatch = step?.companySizeIds?.includes(selection.companySizeId) ?? false;
			const revenueMatch = step?.companyRevenueIds?.includes(selection.companyRevenueId) ?? false;
			const balanceMatch = step?.companyBalanceIds?.includes(selection.companyBalanceId) ?? false;

			return industryMatch && sizeMatch && revenueMatch && balanceMatch;
		});
	}, [selection, questionnaireData]);

	const filteredSelfAssessment = toDos
		.filter((toDo) => {
			return toDo.question && toDo.question.trim() !== "";
		})
		.sort((a, b) => {
			const orderA = a.order ?? 0;
			const orderB = b.order ?? 0;

			if (orderA > orderB) {
				return 1;
			} else if (orderA < orderB) {
				return -1;
			}

			return 0;
		});

	const handleRegister = () => {
		navigate(REGISTRATION, {
			state: {
				selfAssessmentCompleted: true,
			},
		});
	};

	return (
		<div className="w-full flex flex-col min-h-screen">
			<div className="w-full flex justify-center px-4 md:px-0 flex-col flex-1">
				{/* {questionFlow === "initial" && postBusinessFlow === null && (
					<StartQuestionnaire handleStart={handleStart} isLoading={isLoadingQuestionnaire} />
				)} */}
				{questionFlow === "check-cybersecurity" && postBusinessFlow === null && (
					<CybersecurityPrompt handleStart={handleStart} isLoading={false} />
				)}
				{questionFlow === "business-info" && postBusinessFlow === null && !selection && (
					<BusinessInfoFlow
						onComplete={(selection) => {
							setSelection(selection);
							setPostBusinessFlow(initialAnswer === "yes" ? "self-assessment" : "needs-analysis");
						}}
					/>
				)}
				{selection && postBusinessFlow === "needs-analysis" && (
					<NeedsAnalysisFlow
						filteredNeedsAnalysis={filteredNeedsAnalysis}
						onComplete={() => setPostBusinessFlow("self-assessment")}
					/>
				)}
				{selection && postBusinessFlow === "self-assessment" && (
					<SelfAssessmentFlow filteredToDos={filteredSelfAssessment} onComplete={handleRegister} />
				)}
			</div>
		</div>
	);
}
